import { z } from 'zod';
import { AddressSchema, BusinessDetailsSchema, type Address } from '../backend_config.types';
import { JsonValueSchema, PhoneNumberSchema } from './common.schemas';
import { GenericTransactionDetailsSchema } from './payment.schemas';
import {
    DealertrackDealTransactionDetailsV1Schema,
    DealertrackArStatementCustomersTransactionDetailsV1Schema,
    DealertrackLeasingTransactionDetailsV1Schema,
    DealertrackPartsTransactionDetailsV1Schema,
    DealertrackServiceTransactionDetailsV1Schema,
} from './ipw/dealertrack/dealertrack-transaction-details.schemas';
import { StoredMaskedPanSchema } from './masked_pan.schemas';
import {
    CardType,
    type CustomerDetails,
    type GetReceiptDataResponse,
    type ProcessAchPaymentRequest,
    type ProcessAchPaymentResponse,
    type ProcessCardTokenPaymentRequest,
    type ProcessCardTokenPaymentResponse,
    type RecordTokenRequest,
    type RecordTokenResponse,
    type TerminalAbortRequest,
    type TerminalAbortResponse,
    type TerminalSaleRequest,
    type TerminalSaleResponse,
    type TransactionDetails,
    type TransactionDetailsCreateRequest,
    type ValorCardTokenizationResponse,
} from '../samba-payment.types';
import type { PaymentTransactionAmountBreakdown } from '../payment.types';

const UuidSchema: z.ZodString = z.string().uuid();

const NonEmptyStringSchema: z.ZodString = z.string().min(1);

export const CardholderBillingAddressSchema: z.ZodType<Address> = AddressSchema.superRefine(
    (value: Address, ctx: z.RefinementCtx) => {
        if (value.street.trim().length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Billing street is required',
                path: ['street'],
            });
        }
        if (value.city.trim().length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Billing city is required',
                path: ['city'],
            });
        }
        if (value.country === 'US' && value.zip === '00000') {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Billing ZIP must not be 00000',
                path: ['zip'],
            });
        }
    }
);

export const BillingAddressSchema: z.ZodType<Address> = CardholderBillingAddressSchema;

export const SambaGatewayResponseHeaderSchema: z.ZodType<Record<string, string>> =
    z.record(z.string(), z.string());

export const CardTypeSchema: z.ZodType<CardType> = z.union([
    z.literal(CardType.DEBIT),
    z.literal(CardType.CREDIT),
]);

export const TransactionDetailsSchema: z.ZodType<TransactionDetails> = z.union([
    GenericTransactionDetailsSchema,
    DealertrackPartsTransactionDetailsV1Schema,
    DealertrackServiceTransactionDetailsV1Schema,
    DealertrackDealTransactionDetailsV1Schema,
    DealertrackLeasingTransactionDetailsV1Schema,
    DealertrackArStatementCustomersTransactionDetailsV1Schema,
]);

export const CustomerDetailsSchema: z.ZodType<CustomerDetails> = z.object({
    customerName: z.string().optional(),
    mainPhone: PhoneNumberSchema.optional(),
    otherPhones: z.array(PhoneNumberSchema).default([]),
    mainEmail: z.string().email().optional(),
    address: AddressSchema.optional(),
    billingAddress: AddressSchema.optional(),
});

const TransactionDetailsCreateRequestSchema: z.ZodType<
    TransactionDetailsCreateRequest<TransactionDetails>
> = z.lazy(() => TransactionDetailsSchema);

export const TerminalSaleRequestSchema: z.ZodType<TerminalSaleRequest> = z.object({
    terminal_device_uuid: UuidSchema,
    amount: z.string().optional(),
    description: z.string().optional(),
    customer_name: z.string().optional(),
    transaction_details: TransactionDetailsCreateRequestSchema,
    merchant_transaction_id: z.never().optional(),
});

export const TerminalSaleResponseSchema: z.ZodType<TerminalSaleResponse> = z.object({
    payment_transaction_uuid: UuidSchema.optional(),
    gateway_txn_id: z.string().optional(),
    success: z.boolean(),
    error: z.string().optional(),
});

export const TerminalAbortRequestSchema: z.ZodType<TerminalAbortRequest> = z.object({
    terminal_device_uuid: UuidSchema,
});

export const TerminalAbortResponseSchema: z.ZodType<TerminalAbortResponse> = z.object({
    success: z.boolean(),
    error: z.string().optional(),
});

export const ValorCardTokenizationResponseSchema:
    z.ZodType<ValorCardTokenizationResponse> = z
        .object({
            error_no: z.literal('S00'),
            error_code: z.literal('S00'),
            cardToken: NonEmptyStringSchema,
            card_brand: NonEmptyStringSchema,
            card_type: NonEmptyStringSchema.optional(),
            response: z.null(),
        })
        .strict();

export const RecordTokenRequestSchema: z.ZodType<RecordTokenRequest> = z.object({
    customer_details: CustomerDetailsSchema,
    terminal_device_uuid: UuidSchema,
    card_type: CardTypeSchema,
    card_brand: NonEmptyStringSchema,
    masked_pan: StoredMaskedPanSchema,
    cardholder_name: NonEmptyStringSchema,
    gateway_response: ValorCardTokenizationResponseSchema,
});

export const RecordTokenResponseSchema: z.ZodType<RecordTokenResponse> = z.object({
    success: z.boolean(),
    payment_method_token_uuid: UuidSchema,
});

export const ProcessCardTokenPaymentRequestSchema:
    z.ZodType<ProcessCardTokenPaymentRequest> = z.object({
        terminal_device_uuid: UuidSchema,
        token: z.string().min(1),
        card_type: CardTypeSchema,
        card_brand: NonEmptyStringSchema,
        card_last4: z.string().regex(/^\d{4}$/),
        cardholder_name: NonEmptyStringSchema,
        billing_address: BillingAddressSchema.optional(),
        amount: z.string().optional(),
        description: z.string().optional(),
        transaction_details: TransactionDetailsCreateRequestSchema,
        surcharge_amount: z.number().min(0),
        convenience_fee_amount: z.number().min(0),
        total_fee_amount: z.number().min(0),
        total_amount_paid: z.number().min(0),
        merchant_transaction_id: z.never().optional(),
    });

export const ProcessCardTokenPaymentResponseSchema:
    z.ZodType<ProcessCardTokenPaymentResponse> = z.object({
        payment_transaction_uuid: UuidSchema.optional(),
        gateway_txn_id: z.string().optional(),
        success: z.boolean(),
        error: z.string().optional(),
    });

export const ProcessAchPaymentRequestSchema: z.ZodType<ProcessAchPaymentRequest> =
    z.object({
        transaction_details: TransactionDetailsCreateRequestSchema,
        gateway_txn_id: z.string().optional(),
        gateway_request: JsonValueSchema.optional(),
        gateway_response: JsonValueSchema.optional(),
        gateway_response_status: z.number().int().optional(),
        gateway_response_header: SambaGatewayResponseHeaderSchema.optional(),
    });

export const ProcessAchPaymentResponseSchema: z.ZodType<ProcessAchPaymentResponse> =
    z.object({
        success: z.boolean(),
        payment_transaction_uuid: UuidSchema,
    });

export const PaymentTransactionAmountBreakdownSchema:
    z.ZodType<PaymentTransactionAmountBreakdown> = z
        .object({
            base_amount: z.number(),
            surcharge: z.number().optional(),
            convenience_fee: z.number().optional(),
            tax: z.number().optional(),
            donation: z.number().optional(),
            total_amount: z.number().optional(),
        })
        .transform((value): PaymentTransactionAmountBreakdown => ({
            ...value,
            total_amount:
                value.total_amount ??
                Number(
                    (
                        value.base_amount +
                        (value.surcharge ?? 0) +
                        (value.convenience_fee ?? 0) +
                        (value.donation ?? 0) +
                        (value.tax ?? 0)
                    ).toFixed(2)
                ),
        }));

export const GetReceiptDataResponseSchema: z.ZodType<GetReceiptDataResponse> =
    z.object({
        merchant_transaction_id: z.string(),
        receipt_timestamp: z.string(),
        gateway_rrn: z.string().optional(),
        gateway_auth_code: z.string().optional(),
        card_brand: z.string().optional(),
        card_type: CardTypeSchema,
        card_last4: z.string().optional(),
        surcharge_pct: z.number().optional(),
        original_amount: z.string(),
        amount_breakdown: PaymentTransactionAmountBreakdownSchema,
        transactionDetails: z.lazy(() => TransactionDetailsSchema),
        business_details: BusinessDetailsSchema.optional(),
        can_get_receipt: z.boolean(),
        missing_required_fields: z.array(z.string()),
    });
